import { useState, useEffect, useMemo, useCallback } from 'react';

export interface UseTableSearchOptions {
  debounceMs?: number;
}

export function useTableSearch<T>(
  data: T[],
  options: UseTableSearchOptions = {}
) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const { debounceMs = 0 } = options;

  // Handle debouncing if specified
  useEffect(() => {
    if (debounceMs > 0) {
      const timer = setTimeout(() => {
        setDebouncedSearchTerm(searchTerm);
      }, debounceMs);

      return () => clearTimeout(timer);
    } else {
      setDebouncedSearchTerm(searchTerm);
    }
  }, [searchTerm, debounceMs]);

  // Helper function to normalize strings by removing commas
  const normalizeString = useCallback((str: string): string => {
    return str.replace(/,/g, '');
  }, []);

  // Helper function to check if a value matches the search query
  const valueMatchesQuery = useCallback(
    (
      value: string,
      originalQuery: string,
      normalizedQuery: string
    ): boolean => {
      const lowerValue = value.toLowerCase();
      return (
        lowerValue.includes(originalQuery) ||
        normalizeString(lowerValue).includes(normalizedQuery)
      );
    },
    [normalizeString]
  );

  // Helper function to search within nested objects
  const searchInNestedObject = useCallback(
    (
      obj: Record<string, unknown>,
      originalQuery: string,
      normalizedQuery: string
    ): boolean => {
      return Object.values(obj).some(nestedValue => {
        if (!nestedValue) return false;
        const stringValue = String(nestedValue);
        return valueMatchesQuery(stringValue, originalQuery, normalizedQuery);
      });
    },
    [valueMatchesQuery]
  );

  // Helper function to check if a field should be skipped during search
  const shouldSkipField = useCallback(
    (key: string, value: unknown): boolean => {
      return key === 'id' || !value;
    },
    []
  );

  // Helper function to search within a single item
  const itemMatchesQuery = useCallback(
    (
      item: Record<string, unknown>,
      originalQuery: string,
      normalizedQuery: string
    ): boolean => {
      return Object.entries(item).some(([key, value]) => {
        if (shouldSkipField(key, value)) return false;

        // Handle nested objects
        if (typeof value === 'object' && value !== null) {
          return searchInNestedObject(
            value as Record<string, unknown>,
            originalQuery,
            normalizedQuery
          );
        }

        // Handle primitive values
        const stringValue = String(value);
        return valueMatchesQuery(stringValue, originalQuery, normalizedQuery);
      });
    },
    [shouldSkipField, searchInNestedObject, valueMatchesQuery]
  );

  // Perform the actual search filtering
  const filteredData = useMemo(() => {
    const trimmedQuery = debouncedSearchTerm.trim();
    if (!trimmedQuery) {
      return data;
    }

    const lowercaseQuery = trimmedQuery.toLowerCase();
    const normalizedQuery = normalizeString(lowercaseQuery);

    return data.filter(item =>
      itemMatchesQuery(
        item as Record<string, unknown>,
        lowercaseQuery,
        normalizedQuery
      )
    );
  }, [data, debouncedSearchTerm, itemMatchesQuery, normalizeString]);

  const handleSearch = (query: string) => {
    setSearchTerm(query);
  };

  return {
    searchTerm,
    handleSearch,
    filteredData,
  };
}
